// Pantalla minimalista para GRABAR voz y clonarla (ElevenLabs IVC).
// El plugin de Premiere la abre vía openExternal: /grabar-voz?nombre=...
// Reusa la mecánica de MediaRecorder de VoiceCloneCreator.tsx, pero SOLO
// grabar (sin subir archivo), con auth propio (para conservar ?next= si no
// hay sesión) y pantalla de "volvé a Premiere" al terminar.
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Mic,
  Square,
  Play,
  Pause,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { CloneVoiceError, createClonedVoice } from "../lib/clonedVoicesApi";
import {
  MAX_SAMPLE_DURATION_SEC,
  MIN_SAMPLE_DURATION_SEC,
} from "../constants/voiceCloning";

type AudioState =
  | { kind: "idle" }
  | { kind: "recording"; startedAt: number; durationSec: number }
  | {
      kind: "ready";
      blob: Blob;
      fileName: string;
      durationSec: number;
      previewUrl: string;
    };

type SubmitState = "idle" | "submitting" | "success" | "error";

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

const GrabarVoz: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [authChecked, setAuthChecked] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const [nombre, setNombre] = useState(searchParams.get("nombre") || "");
  const [audioState, setAudioState] = useState<AudioState>({ kind: "idle" });
  const [consent, setConsent] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const playerRef = useRef<HTMLAudioElement | null>(null);
  const mountedRef = useRef(true);

  // Auth propio: sin sesión → /auth?next=/grabar-voz?... (conserva el destino).
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      if (!session) {
        const next = "/grabar-voz" + window.location.search;
        window.location.replace("/auth?next=" + encodeURIComponent(next));
        return;
      }
      const uid = session.user.id;
      const { data, error } = await supabase
        .from("profiles")
        .select("id_company")
        .eq("id", uid)
        .single();
      if (cancelled) return;
      if (error || !data?.id_company) {
        setAuthError("No pudimos resolver tu empresa. Reintentá desde Atlas.");
      } else {
        setUserId(uid);
        setCompanyId(data.id_company as string);
      }
      setAuthChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupRecording();
      if (audioState.kind === "ready") URL.revokeObjectURL(audioState.previewUrl);
      if (playerRef.current) playerRef.current.onended = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanupRecording = () => {
    if (recordingTimerRef.current != null) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  };

  const startRecording = async () => {
    try {
      setErrorMessage(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      let mimeType = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "audio/webm";
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      const startedAt = Date.now();
      rec.onstop = () => {
        const durationSec = (Date.now() - startedAt) / 1000;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        if (recordingTimerRef.current != null) {
          window.clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        if (!mountedRef.current) return;
        if (blob.size === 0) {
          setErrorMessage("La grabación quedó vacía, probá de nuevo.");
          setAudioState({ kind: "idle" });
          return;
        }
        const previewUrl = URL.createObjectURL(blob);
        const ext = mimeType.includes("mp4") ? "m4a" : "webm";
        setAudioState({
          kind: "ready",
          blob,
          fileName: `grabacion.${ext}`,
          durationSec,
          previewUrl,
        });
      };
      rec.start();
      setAudioState({ kind: "recording", startedAt, durationSec: 0 });
      recordingTimerRef.current = window.setInterval(() => {
        const elapsed = (Date.now() - startedAt) / 1000;
        setAudioState((prev) =>
          prev.kind === "recording" ? { ...prev, durationSec: elapsed } : prev,
        );
        if (elapsed >= MAX_SAMPLE_DURATION_SEC) stopRecording();
      }, 200);
    } catch (e) {
      setErrorMessage("No pudimos acceder al micrófono. Revisá los permisos.");
      cleanupRecording();
      setAudioState({ kind: "idle" });
    }
  };

  const stopRecording = () => {
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch {}
  };

  const togglePreview = () => {
    if (audioState.kind !== "ready") return;
    if (isPlaying) {
      playerRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    if (!playerRef.current) {
      const a = new Audio(audioState.previewUrl);
      playerRef.current = a;
      a.onended = () => setIsPlaying(false);
    }
    playerRef.current.play().catch(() => setIsPlaying(false));
    setIsPlaying(true);
  };

  const resetAudio = () => {
    if (audioState.kind === "ready") URL.revokeObjectURL(audioState.previewUrl);
    playerRef.current?.pause();
    if (playerRef.current) playerRef.current.onended = null;
    playerRef.current = null;
    setIsPlaying(false);
    setAudioState({ kind: "idle" });
  };

  const canSubmit =
    audioState.kind === "ready" &&
    audioState.durationSec >= MIN_SAMPLE_DURATION_SEC &&
    audioState.durationSec <= MAX_SAMPLE_DURATION_SEC &&
    nombre.trim().length > 0 &&
    consent &&
    submitState !== "submitting" &&
    !!userId &&
    !!companyId;

  const handleSubmit = async () => {
    if (audioState.kind !== "ready" || !userId || !companyId) return;
    setSubmitState("submitting");
    setErrorMessage(null);
    try {
      await createClonedVoice({
        userId,
        companyId,
        nombre,
        audioBlob: audioState.blob,
        audioFileName: audioState.fileName,
        durationSec: audioState.durationSec,
        consentimientoAceptado: consent,
      });
      setSubmitState("success");
    } catch (e) {
      setErrorMessage(
        e instanceof CloneVoiceError
          ? e.message
          : "Hubo un problema técnico. Intentá de nuevo.",
      );
      setSubmitState("error");
    }
  };

  // Mientras chequea sesión.
  if (!authChecked && !authError) {
    return (
      <div className="min-h-[100dvh] bg-surface-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-surface-bg flex flex-col items-center justify-center px-4 py-8 font-sans">
      <img
        src="/images/estudio-atlas-logo-solo.svg"
        alt="Atlas"
        className="w-12 h-12 mb-6"
      />
      <div className="w-full max-w-md bg-surface-card rounded-lg shadow-md p-6">
        {authError ? (
          <div className="text-center py-4">
            <AlertCircle className="w-8 h-8 text-error mx-auto mb-2" />
            <p className="text-[14px] text-brand-black/80">{authError}</p>
          </div>
        ) : submitState === "success" ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-brand-primary mx-auto mb-3" />
            <h2 className="text-[18px] font-semibold text-brand-black">
              ¡Voz creada!
            </h2>
            <p className="text-[14px] text-brand-black/70 mt-2 leading-relaxed">
              Tu voz "{nombre.trim()}" quedó lista. Volvé a Premiere (Alt+Tab) y
              tocá ↻ en "Mis voces" para verla.
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-[20px] font-semibold text-brand-black mb-1">
              Grabá tu voz
            </h1>
            <p className="text-[13px] text-brand-black/60 mb-5">
              Grabá entre 30 segundos y 3 minutos hablando claro, en un lugar
              silencioso.
            </p>

            <label className="block text-[13px] font-medium text-brand-black/70 mb-1.5">
              Nombre de la voz
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Mi voz"
              maxLength={50}
              className="w-full px-3 py-2 rounded-md border border-neutral-200 text-[14px] text-brand-black focus:outline-none focus:border-brand-primary mb-5"
            />

            {audioState.kind === "idle" && (
              <div className="flex flex-col items-center gap-2 mb-5">
                <button
                  type="button"
                  onClick={startRecording}
                  className="w-16 h-16 rounded-full bg-brand-primary hover:bg-brand-primary-hover flex items-center justify-center text-white transition-colors"
                >
                  <Mic className="w-7 h-7" />
                </button>
                <span className="text-[12px] text-brand-black/55">
                  Tocá para grabar
                </span>
              </div>
            )}

            {audioState.kind === "recording" && (
              <div className="flex items-center justify-between gap-2 px-3 py-3 rounded-md bg-red-50 border border-red-200 mb-5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[14px] font-medium text-brand-black/85">
                    Grabando… {formatDuration(audioState.durationSec)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={stopRecording}
                  className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-md bg-red-500 hover:bg-red-600 text-white text-[13px] font-medium"
                >
                  <Square className="w-3.5 h-3.5" />
                  Detener
                </button>
              </div>
            )}

            {audioState.kind === "ready" && (
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md bg-neutral-50 border border-neutral-200 mb-2">
                <button
                  type="button"
                  onClick={togglePreview}
                  aria-label={isPlaying ? "Pausar" : "Reproducir"}
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-brand-primary text-white shrink-0"
                >
                  {isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4 ml-0.5" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-brand-black">
                    Audio listo
                  </p>
                  <p className="text-[12px] text-brand-black/55">
                    Duración: {formatDuration(audioState.durationSec)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resetAudio}
                  className="text-[12px] text-brand-primary hover:underline"
                >
                  Rehacer
                </button>
              </div>
            )}

            {audioState.kind === "ready" &&
              audioState.durationSec < MIN_SAMPLE_DURATION_SEC && (
                <p className="text-[12px] text-error flex items-center gap-1 mb-2">
                  <AlertCircle className="w-3.5 h-3.5" />
                  El audio debe durar al menos 30 segundos.
                </p>
              )}

            <label className="flex items-start gap-2 cursor-pointer mt-4 mb-4">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4"
              />
              <span className="text-[13px] text-brand-black/70 leading-relaxed">
                Confirmo que tengo derechos sobre esta voz y acepto los términos
                de ElevenLabs para clonación.
              </span>
            </label>

            {errorMessage && (
              <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 mb-4">
                <p className="text-[13px] text-red-700 flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{errorMessage}</span>
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-[14.5px] font-semibold transition-colors ${
                canSubmit
                  ? "bg-brand-primary text-white hover:bg-brand-primary-hover"
                  : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
              }`}
            >
              {submitState === "submitting" && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {submitState === "submitting" ? "Creando voz…" : "Enviar"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GrabarVoz;
